import { PlusCircleFilled } from "@ant-design/icons";
import { Button, Select, SelectProps } from "antd";
import { observer } from "mobx-react-lite";
import type { BaseSelectRef } from "rc-select";
import React, { useEffect, useMemo } from "react";
import { RtSearch } from "src/components/ReactiveTable/components/RtSearch";
import {
  AbstractFilters,
  AbstractRow,
} from "src/components/ReactiveTable/RtTypes";

import { MdmDrawer } from "../MdmDrawer";
import { MdmTable, MdmTableSystemTools } from "../MdmTable";
import { RtSelection } from "../ReactiveTable/components/RtSelection";
import { RtColumn } from "../ReactiveTable/RtColumn";
import { ToolBox2 } from "../ToolBox2";
import * as styles from "./RefSelect.module.less";
import { RefSelectStore } from "./RefSelectStore";

type PropsRefSelect<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters & { text?: string },
> = Omit<
  SelectProps,
  "mode" | "onChange" | "onClick" | "onDeselect" | "open" | "options" | "value"
> & {
  ref?: React.Ref<BaseSelectRef>;
  refSelectStore: RefSelectStore<TRow, TFilters>;
  columns: RtColumn<TRow>[];
  value?: unknown[] | unknown;
  onChange?: (value: unknown[] | unknown) => void;
};

export const RefSelect = observer(
  <
    TRow extends AbstractRow,
    TFilters extends AbstractFilters & { text?: string },
  >({
    ref,
    refSelectStore,
    columns,
    value = [],
    onChange,
    style,
    disabled,
    ...props
  }: PropsRefSelect<TRow, TFilters>) => {
    const safeValue = useMemo(() => {
      if (value === undefined || value === null) return [];
      return Array.isArray(value) ? value : [value];
    }, [value]);

    const { tableStore } = refSelectStore.props;
    const { options } = refSelectStore;
    const [open, setOpen] = React.useState(false);
    const isMultiple = tableStore.selectionType === "checkbox";
    const selectionType = isMultiple ? "tags" : undefined;

    useEffect(() => {
      tableStore.setSelectedKeys(safeValue);
    }, []);

    useEffect(() => {
      if (open) {
        tableStore.reload();
        /**
         * это костыль, чтобы при открытии drawer, выбранные значения были синхронизированы
         * потому что при открытии Drawer, выбранные значения сбрасываются в tableStore.init(...)
         * при инициализации таблицы
         */
        tableStore.setSelectedKeys(safeValue);
      }
    }, [open, tableStore]);

    const handleConfirm = () => {
      onChange?.(tableStore.selectedKeys);
      setOpen(false);
    };

    const handleDeselect = (deselectValue: unknown) => {
      const newValue = safeValue.filter((v) => v !== deselectValue);
      onChange?.(newValue);
    };

    const finalProps: SelectProps = {
      ...props,
      options,
      disabled,
      open: false,
      value: safeValue,
      mode: selectionType,
      maxTagCount: "responsive",
      style: { width: "100%", ...style },
      onClick: disabled ? undefined : () => setOpen(true),
      suffixIcon: <PlusCircleFilled className={styles.plusCircleFilled} />,
      onClear: () => {
        tableStore.setSelectedKeys([]);
        onChange?.(undefined);
      },
    };
    const drawerTitle = refSelectStore.props.drawerTitle || "Выберите";

    return (
      <>
        <Select {...finalProps} ref={ref} onDeselect={handleDeselect} />
        <MdmDrawer
          wide
          open={open}
          onClose={() => setOpen(false)}
          title={drawerTitle}
          extra={
            <Button type="primary" onClick={handleConfirm}>
              Выбрать
            </Button>
          }
        >
          <ToolBox2
            mainTool={
              <RtSelection
                store={tableStore}
                makeOption={refSelectStore.makeOption}
                emptyMsg="Ничего не выбрано"
              />
            }
          >
            <ToolBox2
              mainTool={<RtSearch store={tableStore} />}
              extraTools={<MdmTableSystemTools store={tableStore} />}
            >
              <MdmTable store={tableStore} columns={columns} />
            </ToolBox2>
          </ToolBox2>
        </MdmDrawer>
      </>
    );
  },
);
